import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  CheckCircle,
  Circle,
  X,
  Flower2,
  Sprout,
  Leaf
} from 'lucide-react';
import { getReflections } from '../utils/localStorage';

// Month flower configurations
const MONTH_FLOWERS = {
  0: { // January - Snowdrop
    name: 'Snowdrop',
    emoji: '🪻',
    colors: {
      petal1: '#FFFFFF',
      petal2: '#E8F5E9',
      petal3: '#C8E6C9',
      center: '#90A955',
      accent: '#C8E6C9'
    }
  },
  1: { // February - Violet
    name: 'Violet',
    emoji: '🪻',
    colors: {
      petal1: '#E1BEE7',
      petal2: '#CE93D8',
      petal3: '#AB47BC',
      center: '#FFD54F',
      accent: '#9C27B0'
    }
  },
  2: { // March - Daffodil
    name: 'Daffodil',
    emoji: '🌻',
    colors: {
      petal1: '#FFF9C4',
      petal2: '#FFEE58',
      petal3: '#FFD600',
      center: '#FF8F00',
      accent: '#FFB300'
    }
  },
  3: { // April - Tulip
    name: 'Tulip',
    emoji: '🌷',
    colors: {
      petal1: '#FFCDD2',
      petal2: '#EF5350',
      petal3: '#C62828',
      center: '#FFEB3B',
      accent: '#D32F2F'
    }
  },
  4: { // May - Lily of the Valley
    name: 'Lily',
    emoji: '💮',
    colors: {
      petal1: '#FFFFFF',
      petal2: '#F5F5F5',
      petal3: '#EEEEEE',
      center: '#FFF59D',
      accent: '#E0E0E0'
    }
  },
  5: { // June - Rose
    name: 'Rose',
    emoji: '🌹',
    colors: {
      petal1: '#FCE4EC',
      petal2: '#F48FB1',
      petal3: '#EC407A',
      center: '#FFF176',
      accent: '#E91E63'
    }
  },
  6: { // July - Sunflower
    name: 'Sunflower',
    emoji: '🌻',
    colors: {
      petal1: '#FFEE58',
      petal2: '#FFD600',
      petal3: '#FFAB00',
      center: '#5D4037',
      accent: '#FFC107'
    }
  },
  7: { // August - Dahlia
    name: 'Dahlia',
    emoji: '🌺',
    colors: {
      petal1: '#FFCCBC',
      petal2: '#FF8A65',
      petal3: '#E64A19',
      center: '#FFC107',
      accent: '#FF5722'
    }
  },
  8: { // September - Aster
    name: 'Aster',
    emoji: '🪻',
    colors: {
      petal1: '#E3F2FD',
      petal2: '#90CAF9',
      petal3: '#1976D2',
      center: '#FFEB3B',
      accent: '#2196F3'
    }
  },
  9: { // October - Marigold
    name: 'Marigold',
    emoji: '🌼',
    colors: {
      petal1: '#FFE0B2',
      petal2: '#FFB74D',
      petal3: '#F57C00',
      center: '#8D6E63',
      accent: '#FF9800'
    }
  },
  10: { // November - Chrysanthemum
    name: 'Mum',
    emoji: '🌸',
    colors: {
      petal1: '#F3E5F5',
      petal2: '#BA68C8',
      petal3: '#7B1FA2',
      center: '#FFD54F',
      accent: '#9C27B0'
    }
  },
  11: { // December - Poinsettia
    name: 'Poinsettia',
    emoji: '🌺',
    colors: {
      petal1: '#FFCDD2',
      petal2: '#E53935',
      petal3: '#B71C1C',
      center: '#FFD54F',
      accent: '#C62828'
    }
  }
};

// Beautiful Flower SVG Component with monthly variations
const BeautifulFlower = ({ percentage, month = 0 }) => {
  const flowerConfig = MONTH_FLOWERS[month] || MONTH_FLOWERS[0];
  const { colors } = flowerConfig;
  
  // Determine growth stage based on percentage
  let stage = 0;
  if (percentage >= 90) stage = 7; // Full magical bloom
  else if (percentage >= 75) stage = 6; // Large beautiful flower
  else if (percentage >= 60) stage = 5; // Medium flower blooming
  else if (percentage >= 45) stage = 4; // Flower bud opening
  else if (percentage >= 30) stage = 3; // Closed bud
  else if (percentage >= 15) stage = 2; // Sprout with leaves
  else if (percentage > 0) stage = 1; // Tiny sprout

  // Generate unique gradient IDs for this month
  const gradientId = `month${month}`;

  return (
    <svg viewBox="0 0 200 300" className="w-full h-full max-h-64">
      <defs>
        {/* Common Gradients */}
        <linearGradient id="potGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D97706" />
          <stop offset="100%" stopColor="#92400E" />
        </linearGradient>
        <linearGradient id="soilGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#78350F" />
          <stop offset="100%" stopColor="#451A03" />
        </linearGradient>
        <linearGradient id="stemGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#8A9A5B" />
          <stop offset="100%" stopColor="#5F6F48" />
        </linearGradient>
        <linearGradient id="leafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A4B37A" />
          <stop offset="100%" stopColor="#8A9A5B" />
        </linearGradient>
        
        {/* Month-specific flower gradients */}
        <radialGradient id={`${gradientId}Petal1`} cx="30%" cy="30%">
          <stop offset="0%" stopColor={colors.petal1} />
          <stop offset="50%" stopColor={colors.petal2} />
          <stop offset="100%" stopColor={colors.petal3} />
        </radialGradient>
        <radialGradient id={`${gradientId}Petal2`} cx="30%" cy="30%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="40%" stopColor={colors.petal1} />
          <stop offset="100%" stopColor={colors.petal2} />
        </radialGradient>
        <radialGradient id={`${gradientId}Center`} cx="40%" cy="40%">
          <stop offset="0%" stopColor="#FEF9C3" />
          <stop offset="50%" stopColor={colors.center} />
          <stop offset="100%" stopColor={colors.center} />
        </radialGradient>
        <radialGradient id={`${gradientId}Glow`} cx="50%" cy="50%">
          <stop offset="0%" stopColor={colors.petal1} stopOpacity="0.8" />
          <stop offset="100%" stopColor={colors.petal1} stopOpacity="0" />
        </radialGradient>
        
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="glow"/>
          <feMerge>
            <feMergeNode in="glow"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.2"/>
        </filter>
      </defs>

      {/* Pot - Same for all months */}
      <ellipse cx="100" cy="285" rx="35" ry="8" fill="#451A03" opacity="0.3" />
      <path d="M65 250 L70 285 Q100 295 130 285 L135 250 Z" fill="url(#potGradient)" filter="url(#shadow)" />
      <ellipse cx="100" cy="250" rx="35" ry="8" fill="#F59E0B" />
      <ellipse cx="100" cy="252" rx="30" ry="6" fill="url(#soilGradient)" />
      
      {/* Stage 0: Seed */}
      {stage === 0 && (
        <g>
          <ellipse cx="100" cy="248" rx="6" ry="4" fill="#92400E" filter="url(#shadow)" />
          <ellipse cx="98" cy="247" rx="2" ry="1" fill="#B45309" opacity="0.5" />
        </g>
      )}

      {/* Stage 1+: Stem - Same for all months */}
      {stage >= 1 && (
        <g className="animate-fade-in">
          <path 
            d={stage === 1 ? "M100 248 Q100 235 100 225" : "M100 248 Q98 200 100 120"} 
            stroke="url(#stemGradient)" 
            strokeWidth={stage === 1 ? "3" : "5"} 
            fill="none" 
            strokeLinecap="round"
          />
        </g>
      )}

      {/* Growing Leaves - proper leaf shapes, angled upward */}
      {stage >= 2 && (
        <g className="animate-fade-in">
          {/* First pair of leaves */}
          <path d="M99 218 C88 222 74 214 72 201 C76 210 89 216 99 214 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
          <path d="M101 214 C112 218 126 210 128 197 C124 206 111 212 101 210 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
        </g>
      )}

      {/* Second pair - stage 3+ */}
      {stage >= 3 && (
        <g className="animate-fade-in">
          <path d="M99 236 C88 240 74 232 72 219 C76 228 89 234 99 232 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
          <path d="M101 232 C112 236 126 228 128 215 C124 224 111 230 101 228 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
        </g>
      )}

      {/* Third pair - stage 5+ */}
      {stage >= 5 && (
        <g className="animate-fade-in">
          <path d="M99 193 C88 197 74 189 72 176 C76 185 89 191 99 189 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
          <path d="M101 189 C112 193 126 185 128 172 C124 181 111 187 101 185 Z" fill="url(#leafGradient)" filter="url(#shadow)" />
        </g>
      )}

      {/* Stage 3: Closed Bud - Uses month colors */}
      {stage === 3 && (
        <g className="animate-fade-in">
          <ellipse cx="100" cy="115" rx="12" ry="18" fill="#A4B37A" />
          <path d="M92 105 Q100 95 108 105" fill={colors.petal2} opacity="0.5" />
          <path d="M88 115 Q100 100 112 115" fill="#5F6F48" opacity="0.7" />
        </g>
      )}

      {/* Stage 4: Bud Opening - Uses month colors */}
      {stage === 4 && (
        <g className="animate-fade-in">
          <path d="M85 120 Q90 100 100 95 Q110 100 115 120" fill="#8A9A5B" />
          <ellipse cx="92" cy="105" rx="8" ry="12" fill={colors.petal1} transform="rotate(-20, 92, 105)" />
          <ellipse cx="108" cy="105" rx="8" ry="12" fill={colors.petal1} transform="rotate(20, 108, 105)" />
          <ellipse cx="100" cy="98" rx="7" ry="10" fill={colors.petal1} />
          <circle cx="100" cy="108" r="5" fill={colors.center} />
        </g>
      )}

      {/* Stage 5, 6, 7: Flower shapes differ by month */}
      {stage >= 5 && (
        <g className="animate-fade-in" filter="url(#softGlow)">
          {/* JANUARY - Snowdrop: Drooping bell-shaped flowers */}
          {month === 0 && (
            <g>
              {/* Drooping stem */}
              <path d="M100 120 Q95 100 85 95" stroke="url(#stemGradient)" strokeWidth="3" fill="none" />
              {/* Bell-shaped flower */}
              <ellipse cx="80" cy="90" rx={stage >= 7 ? 18 : stage >= 6 ? 14 : 10} ry={stage >= 7 ? 22 : stage >= 6 ? 18 : 14} fill={colors.petal1} />
              <path d={`M${80 - (stage >= 7 ? 15 : 10)} 95 Q80 ${stage >= 7 ? 115 : 105} ${80 + (stage >= 7 ? 15 : 10)} 95`} fill={colors.petal2} />
              <ellipse cx="80" cy="88" rx={stage >= 7 ? 10 : 7} ry={stage >= 7 ? 12 : 9} fill="#FFFFFF" opacity="0.6" />
              {stage >= 6 && <circle cx="80" cy="98" r="3" fill={colors.center} />}
              {stage >= 7 && (
                <>
                  <path d="M100 120 Q110 95 120 90" stroke="url(#stemGradient)" strokeWidth="2" fill="none" />
                  <ellipse cx="123" cy="85" rx="12" ry="16" fill={colors.petal1} />
                  <path d="M115 88 Q123 100 131 88" fill={colors.petal2} />
                </>
              )}
            </g>
          )}

          {/* FEBRUARY - Violet: Small 5-petal flowers */}
          {month === 1 && (
            <g>
              {[0, 72, 144, 216, 288].map((angle, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy={85 - (stage >= 7 ? 20 : stage >= 6 ? 15 : 10)}
                  rx={stage >= 7 ? 12 : stage >= 6 ? 10 : 8}
                  ry={stage >= 7 ? 20 : stage >= 6 ? 16 : 12}
                  fill={i % 2 === 0 ? colors.petal2 : colors.petal3}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              <circle cx="100" cy="95" r={stage >= 7 ? 10 : 7} fill={colors.center} />
              <circle cx="98" cy="93" r="2" fill="#FFFFFF" opacity="0.7" />
              {stage >= 7 && (
                <>
                  <text x="55" y="60" fontSize="10" className="animate-twinkle">✨</text>
                  <text x="140" y="70" fontSize="8" className="animate-twinkle-delayed">✨</text>
                </>
              )}
            </g>
          )}

          {/* MARCH - Daffodil: Trumpet center with 6 radiating petals */}
          {month === 2 && (
            <g>
              {/* 6 pointed petals */}
              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy={70 - (stage >= 7 ? 5 : 0)}
                  rx={stage >= 7 ? 10 : stage >= 6 ? 8 : 6}
                  ry={stage >= 7 ? 28 : stage >= 6 ? 22 : 16}
                  fill={colors.petal1}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Trumpet/corona in center */}
              <ellipse cx="100" cy="95" rx={stage >= 7 ? 18 : 14} ry={stage >= 7 ? 12 : 9} fill={colors.petal3} />
              <ellipse cx="100" cy="92" rx={stage >= 7 ? 14 : 10} ry={stage >= 7 ? 18 : 14} fill={colors.center} />
              <ellipse cx="100" cy="88" rx={stage >= 7 ? 10 : 7} ry={stage >= 7 ? 12 : 9} fill="#FF8F00" />
              {stage >= 7 && <circle cx="100" cy="95" r="5" fill="#5D4037" opacity="0.5" />}
            </g>
          )}

          {/* APRIL - Tulip: Cup-shaped with pointed petals */}
          {month === 3 && (
            <g>
              {/* Back petals */}
              <path d={`M${100 - (stage >= 7 ? 25 : 18)} 110 Q100 ${stage >= 7 ? 50 : 65} ${100 + (stage >= 7 ? 25 : 18)} 110`} fill={colors.petal3} />
              {/* Side petals */}
              <path d={`M${100 - (stage >= 7 ? 30 : 22)} 115 Q${100 - 15} ${stage >= 7 ? 55 : 70} 100 ${stage >= 7 ? 75 : 85}`} fill={colors.petal2} />
              <path d={`M${100 + (stage >= 7 ? 30 : 22)} 115 Q${100 + 15} ${stage >= 7 ? 55 : 70} 100 ${stage >= 7 ? 75 : 85}`} fill={colors.petal2} />
              {/* Front petal */}
              <path d={`M${100 - (stage >= 7 ? 20 : 15)} 115 Q100 ${stage >= 7 ? 60 : 75} ${100 + (stage >= 7 ? 20 : 15)} 115`} fill={colors.petal1} />
              {/* Inner glow */}
              <ellipse cx="100" cy="105" rx={stage >= 7 ? 12 : 8} ry={stage >= 7 ? 8 : 5} fill={colors.center} opacity="0.6" />
              {stage >= 7 && (
                <>
                  <text x="55" y="55" fontSize="10" className="animate-twinkle">✨</text>
                  <text x="140" y="65" fontSize="8" className="animate-twinkle-delayed">✨</text>
                </>
              )}
            </g>
          )}

          {/* MAY - Lily: Trumpet with curving petals */}
          {month === 4 && (
            <g>
              {/* 6 curving petals */}
              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                <path 
                  key={i}
                  d={`M100 95 Q${100 + (stage >= 7 ? 35 : 25)} ${95 - 20} ${100 + (stage >= 7 ? 25 : 18)} ${95 - (stage >= 7 ? 45 : 35)} Q${100 + 10} ${95 - (stage >= 7 ? 55 : 40)} 100 95`}
                  fill={i % 2 === 0 ? colors.petal1 : colors.petal2}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Spotted details for lily */}
              {stage >= 7 && [30, 90, 150, 210, 270, 330].map((angle, i) => (
                <circle key={i} cx="100" cy="70" r="2" fill={colors.accent} transform={`rotate(${angle}, 100, 95)`} opacity="0.6" />
              ))}
              {/* Stamens */}
              <circle cx="100" cy="95" r={stage >= 7 ? 8 : 6} fill={colors.center} />
              {stage >= 7 && (
                <>
                  {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                    <line key={i} x1="100" y1="95" x2="100" y2="80" stroke={colors.center} strokeWidth="1" transform={`rotate(${angle}, 100, 95)`} />
                  ))}
                </>
              )}
            </g>
          )}

          {/* JUNE - Rose: Layered spiral petals */}
          {month === 5 && (
            <g>
              {/* Outer petals */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy={80 - (stage >= 7 ? 5 : 0)}
                  rx={stage >= 7 ? 12 : stage >= 6 ? 10 : 8}
                  ry={stage >= 7 ? 25 : stage >= 6 ? 20 : 15}
                  fill={colors.petal1}
                  transform={`rotate(${angle}, 100, 95)`}
                  opacity="0.9"
                />
              ))}
              {/* Middle petals */}
              {[20, 70, 120, 170, 220, 270, 320].map((angle, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy="85"
                  rx={stage >= 7 ? 10 : 8}
                  ry={stage >= 7 ? 18 : 14}
                  fill={colors.petal2}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Inner spiral */}
              {stage >= 6 && [0, 60, 120, 180, 240, 300].map((angle, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy="90"
                  rx="6" ry="12"
                  fill={colors.petal3}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Center bud */}
              <circle cx="100" cy="95" r={stage >= 7 ? 10 : 7} fill={colors.petal3} />
              <circle cx="98" cy="93" r="3" fill={colors.petal2} />
            </g>
          )}

          {/* JULY - Sunflower: Large disk with ray petals */}
          {month === 6 && (
            <g>
              {/* Ray petals */}
              {Array.from({length: stage >= 7 ? 20 : stage >= 6 ? 16 : 12}, (_, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy={65 - (stage >= 7 ? 5 : 0)}
                  rx={stage >= 7 ? 8 : 6}
                  ry={stage >= 7 ? 25 : stage >= 6 ? 20 : 15}
                  fill={i % 2 === 0 ? colors.petal1 : colors.petal2}
                  transform={`rotate(${i * (360 / (stage >= 7 ? 20 : stage >= 6 ? 16 : 12))}, 100, 95)`}
                />
              ))}
              {/* Large brown center disk */}
              <circle cx="100" cy="95" r={stage >= 7 ? 25 : stage >= 6 ? 20 : 15} fill={colors.center} />
              {/* Seed pattern */}
              {stage >= 6 && (
                <g>
                  {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
                    <circle key={i} cx="100" cy="88" r="2" fill="#3E2723" transform={`rotate(${angle}, 100, 95)`} />
                  ))}
                  <circle cx="100" cy="95" r="5" fill="#4E342E" />
                </g>
              )}
            </g>
          )}

          {/* AUGUST - Dahlia: Ball with layered pointed petals */}
          {month === 7 && (
            <g>
              {/* Outer pointed petals */}
              {Array.from({length: 16}, (_, i) => (
                <path 
                  key={i}
                  d={`M100 95 L${100 + (stage >= 7 ? 8 : 6)} ${95 - (stage >= 7 ? 35 : 25)} L100 ${95 - (stage >= 7 ? 40 : 30)} L${100 - (stage >= 7 ? 8 : 6)} ${95 - (stage >= 7 ? 35 : 25)} Z`}
                  fill={i % 2 === 0 ? colors.petal1 : colors.petal2}
                  transform={`rotate(${i * 22.5}, 100, 95)`}
                />
              ))}
              {/* Middle layer */}
              {stage >= 6 && Array.from({length: 12}, (_, i) => (
                <path 
                  key={i}
                  d={`M100 95 L${100 + 5} ${95 - 22} L100 ${95 - 26} L${100 - 5} ${95 - 22} Z`}
                  fill={colors.petal3}
                  transform={`rotate(${i * 30 + 15}, 100, 95)`}
                />
              ))}
              {/* Center */}
              <circle cx="100" cy="95" r={stage >= 7 ? 12 : 9} fill={colors.center} />
              <circle cx="98" cy="93" r="3" fill="#FFF59D" opacity="0.8" />
            </g>
          )}

          {/* SEPTEMBER - Aster: Daisy-like thin petals */}
          {month === 8 && (
            <g>
              {/* Many thin ray petals */}
              {Array.from({length: stage >= 7 ? 30 : stage >= 6 ? 24 : 18}, (_, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy={70 - (stage >= 7 ? 5 : 0)}
                  rx={stage >= 7 ? 4 : 3}
                  ry={stage >= 7 ? 25 : stage >= 6 ? 20 : 15}
                  fill={i % 3 === 0 ? colors.petal1 : i % 3 === 1 ? colors.petal2 : colors.petal3}
                  transform={`rotate(${i * (360 / (stage >= 7 ? 30 : stage >= 6 ? 24 : 18))}, 100, 95)`}
                />
              ))}
              {/* Yellow center */}
              <circle cx="100" cy="95" r={stage >= 7 ? 12 : 9} fill={colors.center} />
              <circle cx="97" cy="92" r="3" fill="#FFF59D" />
              <circle cx="103" cy="94" r="2" fill="#FFF59D" opacity="0.7" />
            </g>
          )}

          {/* OCTOBER - Marigold: Pompom ruffled petals */}
          {month === 9 && (
            <g>
              {/* Ruffled outer petals */}
              {Array.from({length: 20}, (_, i) => (
                <ellipse 
                  key={i}
                  cx={100 + Math.sin(i * 0.8) * 3} 
                  cy={75 + Math.cos(i * 0.5) * 2}
                  rx={stage >= 7 ? 10 : 8}
                  ry={stage >= 7 ? 18 : 14}
                  fill={i % 2 === 0 ? colors.petal1 : colors.petal2}
                  transform={`rotate(${i * 18}, 100, 95)`}
                />
              ))}
              {/* Inner ruffled layer */}
              {stage >= 6 && Array.from({length: 14}, (_, i) => (
                <ellipse 
                  key={i}
                  cx="100" cy="82"
                  rx="7" ry="12"
                  fill={colors.petal3}
                  transform={`rotate(${i * 25.7 + 10}, 100, 95)`}
                />
              ))}
              {/* Dense center */}
              <circle cx="100" cy="95" r={stage >= 7 ? 10 : 7} fill={colors.center} />
            </g>
          )}

          {/* NOVEMBER - Chrysanthemum: Many thin radiating petals */}
          {month === 10 && (
            <g>
              {/* Outer long petals */}
              {Array.from({length: stage >= 7 ? 36 : 28}, (_, i) => (
                <path 
                  key={i}
                  d={`M100 95 Q${100 + 3} ${95 - 15} ${100 + (stage >= 7 ? 2 : 1)} ${95 - (stage >= 7 ? 40 : 30)} Q100 ${95 - (stage >= 7 ? 42 : 32)} ${100 - (stage >= 7 ? 2 : 1)} ${95 - (stage >= 7 ? 40 : 30)} Q${100 - 3} ${95 - 15} 100 95`}
                  fill={i % 3 === 0 ? colors.petal1 : i % 3 === 1 ? colors.petal2 : colors.petal3}
                  transform={`rotate(${i * (360 / (stage >= 7 ? 36 : 28))}, 100, 95)`}
                />
              ))}
              {/* Inner shorter petals */}
              {stage >= 6 && Array.from({length: 20}, (_, i) => (
                <path 
                  key={i}
                  d={`M100 95 Q${100 + 2} ${95 - 10} 100 ${95 - 22} Q${100 - 2} ${95 - 10} 100 95`}
                  fill={colors.petal2}
                  transform={`rotate(${i * 18 + 5}, 100, 95)`}
                />
              ))}
              {/* Tight center */}
              <circle cx="100" cy="95" r="8" fill={colors.center} />
            </g>
          )}

          {/* DECEMBER - Poinsettia: Star-shaped bracts */}
          {month === 11 && (
            <g>
              {/* Large star-shaped bracts (leaves) */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                <path 
                  key={i}
                  d={`M100 95 Q${100 + 15} ${95 - 10} ${100 + (stage >= 7 ? 35 : 25)} ${95 - (stage >= 7 ? 45 : 35)} Q${100 + 10} ${95 - 25} 100 95`}
                  fill={i % 2 === 0 ? colors.petal2 : colors.petal3}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Inner smaller bracts */}
              {stage >= 6 && [20, 70, 110, 160, 200, 250, 290, 340].map((angle, i) => (
                <path 
                  key={i}
                  d={`M100 95 Q${100 + 10} ${95 - 8} ${100 + 20} ${95 - 28} Q${100 + 5} ${95 - 15} 100 95`}
                  fill={colors.petal1}
                  transform={`rotate(${angle}, 100, 95)`}
                />
              ))}
              {/* Yellow flower cluster in center */}
              <circle cx="100" cy="95" r={stage >= 7 ? 10 : 7} fill={colors.center} />
              {stage >= 7 && (
                <>
                  <circle cx="96" cy="92" r="3" fill="#FDD835" />
                  <circle cx="104" cy="93" r="3" fill="#FDD835" />
                  <circle cx="100" cy="98" r="3" fill="#FDD835" />
                </>
              )}
            </g>
          )}

          {/* Sparkles for stage 7 */}
          {stage === 7 && (
            <>
              <text x="50" y="55" fontSize="10" className="animate-twinkle">✨</text>
              <text x="145" y="60" fontSize="8" className="animate-twinkle-delayed">✨</text>
              <text x="40" y="110" fontSize="8" className="animate-twinkle">⭐</text>
              <text x="155" y="115" fontSize="7" className="animate-twinkle-delayed">⭐</text>
            </>
          )}
        </g>
      )}
    </svg>
  );
};

// Get flower message based on month and percentage
const getFlowerMessage = (percentage, month) => {
  const flowerConfig = MONTH_FLOWERS[month] || MONTH_FLOWERS[0];
  const flowerName = flowerConfig.name;
  const emoji = flowerConfig.emoji;
  
  if (percentage >= 90) return { text: `Your ${flowerName} is in full bloom! ${emoji}`, color: "text-pink-500" };
  if (percentage >= 75) return { text: `${flowerName} blooming beautifully!`, color: "text-pink-400" };
  if (percentage >= 60) return { text: `Your ${flowerName} is opening!`, color: "text-pink-400" };
  if (percentage >= 45) return { text: "A bud is forming!", color: "text-sage" };
  if (percentage >= 30) return { text: "Growing strong!", color: "text-sage" };
  if (percentage >= 15) return { text: "Sprouting nicely!", color: "text-sage-light" };
  if (percentage > 0) return { text: "Just sprouted!", color: "text-sage-light" };
  return { text: "Plant your first seed!", color: "text-amber-500" };
};

// Main Flower Stage Component
const FlowerStage = ({ totalDays, completedDays, month }) => {
  const percentage = totalDays > 0 ? (completedDays / totalDays) * 100 : 0;
  const flowerConfig = MONTH_FLOWERS[month] || MONTH_FLOWERS[0];
  const message = getFlowerMessage(percentage, month);
  
  return (
    <div className="relative w-full flex flex-col items-center">
      <p className="text-xs text-ink-500 font-accent mb-1">
        {flowerConfig.name} {flowerConfig.emoji}
      </p>
      <div className="w-full h-64">
        <BeautifulFlower percentage={percentage} month={month} />
      </div>
      
      {/* Progress text */}
      <div className="text-center mt-2">
        <p className="text-sm font-medium text-ink-700">
          {completedDays}/{totalDays} days
        </p>
        <p className={`text-sm font-accent italic ${message.color}`}>
          {message.text}
        </p>
      </div>
    </div>
  );
};

const ReviewModal = ({ reflection, onClose }) => {
  if (!reflection) return null;
  
  const date = new Date(reflection.date);
  const formattedDate = date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
  
  const modeEmoji = reflection.dayMode === 'survival' ? '🔴' : 
                    reflection.dayMode === 'normal' ? '🟡' : '🟢';
  const modeName = reflection.dayMode === 'survival' ? 'Survival Day' :
                   reflection.dayMode === 'normal' ? 'Normal Day' : 'Good Day';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card 
        className="w-full max-w-md p-6 bg-paper-light shadow-lifted-lg animate-fade-in relative"
        onClick={e => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </Button>
        
        <div className="text-center mb-4">
          <p className="text-sm text-ink-500 font-accent">{formattedDate}</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span>{modeEmoji}</span>
            <span className="font-serif text-ink-700">{modeName}</span>
          </div>
        </div>
        
        <div className="space-y-4">
          {reflection.hardThings && (
            <div className="p-4 bg-survival-bg rounded-lg border border-survival-border">
              <p className="text-xs text-survival-text font-accent mb-1">What was hard:</p>
              <p className="font-handwritten text-lg text-ink-700">{reflection.hardThings}</p>
            </div>
          )}
          
          {reflection.accomplishments && (
            <div className="p-4 bg-good-bg rounded-lg border border-good-border">
              <p className="text-xs text-good-text font-accent mb-1">What I did:</p>
              <p className="font-handwritten text-lg text-ink-700">{reflection.accomplishments}</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 p-3 bg-paper-aged rounded-lg text-center">
          <p className="text-ink-600 font-accent italic text-sm">
            "You did enough that day." 💛
          </p>
        </div>
      </Card>
    </div>
  );
};

const JournalCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reflections, setReflections] = useState([]);
  const [selectedReflection, setSelectedReflection] = useState(null);
  
  useEffect(() => {
    setReflections(getReflections());
  }, []);
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDay = firstDayOfMonth.getDay();
  
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const flowerConfig = MONTH_FLOWERS[month];
  
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  
  const getReflectionForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return reflections.find(r => r.date === dateStr);
  };
  
  // Count completed days in current month
  const completedDaysInMonth = Array.from({ length: daysInMonth }, (_, i) => i + 1)
    .filter(day => getReflectionForDate(day))
    .length;
  
  // Calculate days passed for flower growth calculation
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const isFutureMonth = new Date(year, month, 1) > today;
  
  // For flower calculation:
  // - Past months: use total days in month (flower is "locked in")
  // - Current month: use days passed so far
  // - Future months: use 0 (no growth possible yet)
  let daysForFlower;
  if (isFutureMonth) {
    daysForFlower = 0; // Future month - no days to count yet
  } else if (isCurrentMonth) {
    daysForFlower = today.getDate(); // Current month - days passed
  } else {
    daysForFlower = daysInMonth; // Past month - full month
  }
  
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };
  
  const isPast = (day) => {
    const checkDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return checkDate < today;
  };
  
  return (
    <div className="space-y-6" data-testid="journal-calendar">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            My Journey
          </h2>
          <p className="text-ink-500 font-accent italic">
            Grow a different flower each month - build your garden! 🌸
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center border border-pink-200">
          <Flower2 className="w-6 h-6 text-pink-500" />
        </div>
      </div>
      
      {/* Main content - Calendar and Flower side by side on larger screens */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 p-4 md:p-6 bg-paper-light shadow-lifted border border-stone-200">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevMonth}
              className="text-ink-500 hover:text-ink-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h3 className="font-serif text-xl text-ink-900">{monthName}</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextMonth}
              className="text-ink-500 hover:text-ink-700"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Days header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {days.map(day => (
              <div key={day} className="text-center text-xs text-ink-500 font-accent py-1">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before first of month */}
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            
            {/* Day cells */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const reflection = getReflectionForDate(day);
              const hasReflection = !!reflection;
              const todayClass = isToday(day);
              const pastDay = isPast(day);
              
              return (
                <button
                  key={day}
                  onClick={() => reflection && setSelectedReflection(reflection)}
                  disabled={!hasReflection}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center
                    text-sm transition-all relative
                    ${todayClass 
                      ? 'bg-sage/20 border-2 border-sage font-bold' 
                      : hasReflection 
                        ? 'bg-good-bg border border-good-border hover:scale-105 cursor-pointer' 
                        : pastDay 
                          ? 'bg-stone-100 text-ink-400' 
                          : 'bg-white border border-stone-200'
                    }
                  `}
                >
                  <span className={todayClass ? 'text-sage' : ''}>{day}</span>
                  {hasReflection ? (
                    <CheckCircle className="w-3 h-3 text-sage mt-0.5" />
                  ) : pastDay ? (
                    <Circle className="w-3 h-3 text-ink-300 mt-0.5" />
                  ) : null}
                </button>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-ink-500">
            <div className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-sage" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-1">
              <Circle className="w-3 h-3 text-ink-300" />
              <span>Missed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border-2 border-sage" />
              <span>Today</span>
            </div>
          </div>
        </Card>
        
        {/* Flower Display */}
        <Card className="p-4 md:p-6 bg-paper-light shadow-lifted border border-stone-200">
          <h3 className="font-serif text-lg text-ink-900 mb-1 text-center">
            {monthName.split(' ')[0]}'s Flower
          </h3>
          <p className="text-xs text-ink-500 text-center mb-2 font-accent italic">
            Complete daily reviews to help it bloom
          </p>
          
          <FlowerStage 
            totalDays={daysForFlower} 
            completedDays={completedDaysInMonth}
            month={month}
          />
        </Card>
      </div>
      
      {/* Stats */}
      <Card className="p-4 md:p-6 bg-paper-light shadow-lifted border border-stone-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-serif text-2xl md:text-3xl text-sage">{completedDaysInMonth}</p>
            <p className="text-xs text-ink-500 font-accent">Days closed</p>
          </div>
          <div>
            <p className="font-serif text-2xl md:text-3xl text-ink-700">{daysInMonth - completedDaysInMonth}</p>
            <p className="text-xs text-ink-500 font-accent">Days remaining</p>
          </div>
          <div>
            <p className="font-serif text-2xl md:text-3xl text-brown">
              {daysForFlower > 0 ? Math.round((completedDaysInMonth / daysForFlower) * 100) : 0}%
            </p>
            <p className="text-xs text-ink-500 font-accent">Consistency</p>
          </div>
        </div>
      </Card>
      
      {/* Garden Preview - Show all 12 flowers */}
      <Card className="p-4 md:p-6 bg-paper-light shadow-lifted border border-stone-200">
        <h3 className="font-serif text-lg text-ink-900 mb-3 text-center">
          Your Flower Garden 🌷
        </h3>
        <p className="text-xs text-ink-500 text-center mb-4 font-accent italic">
          A different flower blooms each month
        </p>
        
        <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
          {Object.entries(MONTH_FLOWERS).map(([monthIdx, flower]) => {
            const monthNum = parseInt(monthIdx);
            const isCurrentMonthFlower = monthNum === month;
            const monthDate = new Date(year, monthNum, 1);
            const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short' });
            
            return (
              <div 
                key={monthIdx}
                className={`
                  text-center p-2 rounded-lg transition-all cursor-pointer
                  ${isCurrentMonthFlower 
                    ? 'bg-sage/10 border-2 border-sage scale-105' 
                    : 'bg-white border border-stone-200 hover:border-stone-300'
                  }
                `}
                onClick={() => setCurrentDate(new Date(year, monthNum, 1))}
              >
                <p className="text-xs font-serif font-medium text-ink-700">{monthLabel}</p>
                <p className="text-[10px] text-ink-500 font-accent">{flower.name}</p>
              </div>
            );
          })}
        </div>
      </Card>
      
      {/* Motivational message */}
      <Card className="p-4 bg-warmth-sand border border-stone-300">
        <p className="text-center font-handwritten text-lg text-ink-700">
          Start your journey today. Every day you close is a seed you plant. 🌱
        </p>
      </Card>
      
      {/* Modal */}
      {selectedReflection && (
        <ReviewModal 
          reflection={selectedReflection} 
          onClose={() => setSelectedReflection(null)} 
        />
      )}
    </div>
  );
};

export default JournalCalendar;
